import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, X, ArrowUpRight, ShieldCheck, Lightning, GoogleLogo, FacebookLogo, InstagramLogo } from "@phosphor-icons/react";
import VisibilityEnrollModal from "@/components/site/VisibilityEnrollModal";
import { selectInterest, inquiryHref } from "@/lib/inquiry";
import { PRICES } from "@/lib/visibility";

const included = [
  "Initial Google Business Profile setup or cleanup (one-time onboarding)",
  "Four content pieces per month from photos or clips you provide — prepared for posting with captions, simple branding, and light edits where needed",
  "Each piece is published to your Google Business Profile and one included social platform — Facebook or Instagram. That's up to four posts on each platform, eight placements in total",
  "Basic profile upkeep",
  "A brief monthly activity summary — published posts with links, profile info we updated, and what we need from you next month",
];

const notIncluded = [
  "Filming or photo shoots",
  "Comment or inbox management",
  "Review replies",
  "Paid advertising",
  "Separate original content campaigns for each platform",
  "Elaborate custom design, extensive video editing, animation, or multiple creative versions",
  "Client portal access (included only when WebJesters builds your website)",
];

const PLATFORM_PRICING = [
  { label: "Google Business Profile + Facebook", price: PRICES.monthly, icons: [GoogleLogo, FacebookLogo] },
  { label: "Google Business Profile + Instagram", price: PRICES.monthly, icons: [GoogleLogo, InstagramLogo] },
  { label: "Google Business Profile + Facebook + Instagram", price: PRICES.monthly + PRICES.addon, icons: [GoogleLogo, FacebookLogo, InstagramLogo], note: `+$${PRICES.addon}/mo` },
];

const goodToKnow = [
  [
    "Sending us content",
    "Text or email your photos, clips, and business updates. For the best quality, send originals by email or a download link (Google Drive, Dropbox, iCloud) — regular text attachments get compressed.",
  ],
  [
    "We prepare, you approve",
    "We prepare your photos and short clips for posting, with captions, simple branding, and light edits where needed — then schedule and publish. Nothing goes live without your okay.",
  ],
  [
    "Light prep, not custom design",
    "Depending on the material, that can mean a simple text overlay, your logo, cropping, a basic before-and-after layout, or trimming a short clip. We use these where they help — plenty of posts go out as a clean, well-captioned photo.",
  ],
  [
    "Your originals stay untouched",
    "We keep every file exactly as you sent it. Edits and platform-sized versions are made on copies — the originals are never replaced.",
  ],
  [
    "Adding a second platform",
    `Add Facebook or Instagram alongside your included platform for $${PRICES.addon}/month. It receives the same four prepared pieces, adapted for that platform — captions, sizing, scheduling, and publishing — not four additional pieces. We only bill for it once that account's access is ready and posting can begin.`,
  ],
  [
    "Switching or removing platforms",
    "Changes to your platforms take effect starting the next billing month. For additions or switches, we need manager access to the new account before that month begins.",
  ],
  [
    "Account access during onboarding",
    "You keep ownership of everything. You add WebJesters as a manager on your Google Business Profile and your Facebook Page; Instagram must be a professional account linked to that Page. We never ask for passwords.",
  ],
  [
    "Unused pieces don't roll over",
    "If we don't have the content or approvals needed to publish, those pieces don't carry into the next month. If we miss a piece that was included, we still owe you that work.",
  ],
  [
    `Already did our $${PRICES.gbpSetup} setup?`,
    `If you've completed our standalone Google Business Profile setup, the $${PRICES.onboarding} onboarding fee is waived when you add Local Visibility — provided no substantial new setup work is needed. Enter the same email at checkout and it's applied automatically.`,
  ],
  [
    "Onboarding fee & verification",
    `The $${PRICES.onboarding} onboarding fee is refundable before work begins and non-refundable once setup or cleanup starts. Google approval or verification is never guaranteed.`,
  ],
];

function GbpPostCard() {
  return (
    <div data-testid="local-visibility-gbp-card" className="w-full h-full bg-white text-[#1f1f1f] flex flex-col font-sans">
      <div className="flex items-center gap-2.5 px-3.5 pt-3.5 pb-2.5">
        <img src="/local-visibility/cab-reset-avatar.png" alt="" width="36" height="36" className="w-9 h-9 rounded-full" />
        <div className="min-w-0 leading-tight">
          <div className="flex items-center gap-1 text-[13px] font-medium">
            Cab Reset AZ
            <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-[#1a73e8] text-white text-[9px] leading-none">✓</span>
          </div>
          <div className="text-[11px] text-[#5f6368]">Aug 9, 2026</div>
        </div>
      </div>
      <div className="relative flex-1 min-h-0 bg-[#f1f3f4]">
        <img src="/local-visibility/cab-reset-gbp-photo.jpg" alt="Cab Reset AZ Google Business Profile post — interior detail photo" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        <span className="absolute top-2.5 right-2.5 rounded-full bg-black/60 text-white text-[10px] px-2 py-0.5">1/9</span>
      </div>
      <div className="px-3.5 py-3 text-[12px] leading-snug">
        Quick before &amp; after from an interior detail today. A lot of built-up dirt and grime came out of this one.
        Cleaned up great and made a huge difference.
        <div className="mt-2 text-[#1a73e8] text-[12px]">Learn more</div>
      </div>
    </div>
  );
}

const samples = [
  { kind: "gbp", client: "Cab Reset AZ", note: "Original photo + caption · no edits needed" },
  { src: "/local-visibility/cab-reset-foggy-to-factory.jpg", alt: "Cab Reset AZ social post — headlight restoration before and after", client: "Cab Reset AZ", note: "Before-and-after layout from job photos" },
  { src: "/local-visibility/cab-reset-meet-greg.jpg", alt: "Cab Reset AZ social post — Meet Greg, owner-operator", client: "Cab Reset AZ", note: "Text overlay + logo on the owner's photo" },
  { src: "/local-visibility/scrapmans-metal-into-money.jpg", alt: "Scrapman's Metals social post — Metal into money", client: "Scrapman's Metals", note: "Simple branded promo from a client photo" },
];

const MOBILE_NOTES = 4;

const Eyebrow = ({ children }) => (
  <div className="flex items-center gap-3 text-[11.5px] tracking-[0.22em] uppercase text-brand-700">
    <span className="block w-8 h-px bg-brand-700" />
    {children}
  </div>
);

export default function LocalVisibility() {
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [showAllNotes, setShowAllNotes] = useState(false);
  return (
    <section
      id="local-visibility"
      data-tone="light"
      data-testid="local-visibility-section"
      className="tone-light relative pt-14 pb-14 sm:pt-24 sm:pb-24 md:pt-32 md:pb-32 bg-[#f6f5f1] border-t border-zinc-200 scroll-mt-16 md:scroll-mt-20"
    >
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
        <div className="max-w-2xl">
          <Eyebrow>
            Local Visibility
            <span className="hidden sm:inline normal-case tracking-normal text-zinc-600">· Standalone monthly service</span>
          </Eyebrow>
          <h2 className="mt-5 font-display font-bold text-[34px] sm:text-5xl lg:text-[56px] leading-[1.02] tracking-tight text-zinc-900">
            Stay visible where locals are looking.
          </h2>
          <p className="mt-5 text-zinc-600 text-[15px] sm:text-base leading-relaxed">
            We keep your Google Business Profile active and your social presence consistent. We prepare your photos and
            short clips for posting, with captions, simple branding, and light edits where needed. Billed on its own: take it with a WebJesters website, with a site hosted
            elsewhere, or with no website at all.
          </p>
        </div>

        <div className="mt-9 sm:mt-16 grid lg:grid-cols-12 gap-6 lg:gap-10 items-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            data-testid="local-visibility-card"
            className="lg:col-span-5 relative rounded-3xl border border-zinc-900/10 bg-gradient-to-b from-[#121214] to-[#0a0a0c] overflow-hidden shadow-[0_40px_90px_-30px_rgba(24,24,27,0.5)]"
          >
            <div className="absolute -top-32 -right-24 w-80 h-80 rounded-full bg-emerald-500/[0.12] blur-3xl pointer-events-none" />
            <div className="relative p-6 sm:p-10">
              <div className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] tracking-[0.16em] uppercase text-zinc-300">
                Standalone · no website required
              </div>

              <div className="mt-7 grid gap-5">
                <div className="flex items-baseline gap-x-3">
                  <span className="font-display text-5xl sm:text-6xl tracking-tighter text-white leading-none whitespace-nowrap">${PRICES.onboarding}</span>
                  <span className="text-zinc-400 text-[15px] sm:text-base leading-snug">
                    one-time onboarding
                    <span className="block text-[12.5px] text-zinc-400">Google Business Profile setup or cleanup</span>
                  </span>
                </div>
                <div className="h-px w-full bg-white/[0.06]" />
                <div className="flex items-baseline gap-x-3">
                  <span className="font-display text-5xl sm:text-6xl tracking-tighter text-white leading-none whitespace-nowrap">${PRICES.monthly}</span>
                  <span className="text-zinc-400 text-[15px] sm:text-base leading-snug">
                    per month
                    <span className="block text-[12.5px] text-zinc-400">Google + one social platform · both for ${PRICES.monthly + PRICES.addon}</span>
                  </span>
                </div>
              </div>

              <p data-testid="local-visibility-subtitle" className="mt-7 text-zinc-300 text-[14.5px] sm:text-[15px] leading-relaxed">
                The ${PRICES.onboarding} onboarding is paid up front. The monthly fee begins once setup is complete and we
                have the account access and content needed to start posting. Month-to-month. Cancel anytime.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
                <button
                  type="button"
                  onClick={() => setEnrollOpen(true)}
                  data-testid="local-visibility-setup-cta"
                  className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors duration-200 whitespace-nowrap"
                >
                  <Lightning size={15} weight="fill" className="text-emerald-600" />
                  Start Local Visibility
                  <ArrowUpRight size={16} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <a
                  href={inquiryHref("local_visibility")}
                  onClick={() => selectInterest("local_visibility")}
                  data-testid="local-visibility-cta"
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-white/15 bg-white/[0.03] text-white hover:bg-brand-700/25 hover:border-brand-500/50 transition-colors duration-200 text-[14px] whitespace-nowrap"
                >
                  Talk to us first
                </a>
              </div>

              <div className="mt-6 flex items-center gap-1.5 text-[12.5px] text-zinc-400">
                <ShieldCheck size={13} weight="regular" className="text-emerald-400/80" />
                No long-term contract · Onboarding fee refundable before work begins
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 grid sm:grid-cols-2 gap-9 lg:gap-10 pt-4 lg:pt-2"
          >
            <div data-testid="local-visibility-included">
              <Eyebrow>Included</Eyebrow>
              <ul className="mt-5 grid gap-y-3">
                {included.map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <Check size={14} weight="bold" className="mt-1 flex-shrink-0 text-emerald-600" />
                    <span className="text-[14px] sm:text-[14.5px] text-zinc-700 leading-relaxed">{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div data-testid="local-visibility-not-included">
              <Eyebrow>Not included</Eyebrow>
              <ul className="mt-5 grid gap-y-3">
                {notIncluded.map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <X size={14} weight="bold" className="mt-1 flex-shrink-0 text-zinc-400" />
                    <span className="text-[14px] sm:text-[14.5px] text-zinc-600 leading-relaxed">{line}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[13px] text-zinc-600 leading-relaxed">
                You keep ownership of your Google Business Profile and social accounts. We work through manager access you grant.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Platform pricing */}
        <div className="mt-12 sm:mt-20" data-testid="local-visibility-platforms">
          <Eyebrow>Platforms &amp; monthly pricing</Eyebrow>
          <p className="mt-4 text-[14px] sm:text-[14.5px] text-zinc-600 leading-relaxed max-w-2xl">
            Choose Facebook or Instagram as your included platform. Add the other for ${PRICES.addon}/month — it gets
            adapted versions of the same four monthly pieces, not four extra ones.
          </p>
          <div className="mt-7 card-light rounded-2xl divide-y divide-zinc-200 overflow-hidden">
            {PLATFORM_PRICING.map((row, i) => (
              <div key={row.label} data-testid={`lv-platform-row-${i}`} className="flex items-center justify-between gap-3 px-4 py-4 sm:p-5 sm:px-7">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="hidden sm:flex items-center gap-1.5 text-zinc-600">
                    {row.icons.map((Icon, j) => <Icon key={Icon.displayName || j} size={15} weight={j === 0 ? "bold" : "regular"} />)}
                  </span>
                  <span className="text-[14px] sm:text-[15px] text-zinc-900">{row.label}</span>
                </div>
                <div className="flex items-baseline gap-2 whitespace-nowrap">
                  {row.note && <span className="text-[12px] text-zinc-600">{row.note}</span>}
                  <span className="font-display font-bold text-2xl tracking-tight text-zinc-900">${row.price}</span>
                  <span className="text-[13px] text-zinc-600">/mo</span>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-[13px] text-zinc-600 leading-relaxed">
            Four content pieces published to two platforms means up to four posts on each — eight placements a month.
            With a second platform added, the same four pieces reach three platforms.
          </p>
        </div>

        {/* Good to know */}
        <div className="mt-12 sm:mt-20" data-testid="local-visibility-good-to-know">
          <Eyebrow>How it runs each month</Eyebrow>
          <div className="mt-6 sm:mt-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-6 sm:gap-y-8">
            {goodToKnow.map(([h, p], i) => (
              <motion.div
                key={h}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`local-visibility-note-${i}`}
                className={!showAllNotes && i >= MOBILE_NOTES ? "hidden sm:block" : undefined}
              >
                <h3 className="text-[14.5px] sm:text-[15px] font-medium text-zinc-900 leading-snug">{h}</h3>
                <p className="mt-2.5 text-[14px] sm:text-[14.5px] text-zinc-600 leading-relaxed">{p}</p>
              </motion.div>
            ))}
          </div>
          {!showAllNotes && (
            <button
              type="button"
              onClick={() => setShowAllNotes(true)}
              data-testid="local-visibility-notes-more"
              className="sm:hidden mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-zinc-300 bg-white text-[13.5px] text-zinc-900"
            >
              Show all {goodToKnow.length} details
            </button>
          )}
        </div>

        {/* Sample content */}
        <div className="mt-12 sm:mt-20" data-testid="local-visibility-samples">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <Eyebrow>Recent content</Eyebrow>
              <p className="mt-4 text-[14px] sm:text-[14.5px] text-zinc-600 leading-relaxed max-w-xl">
                Posts we&apos;ve made for our own business and our clients. These show possible treatments, not the standard for every
                post — some get a simple layout or overlay, others go out as the original photo with a good caption.
              </p>
            </div>
            <span className="text-[12.5px] text-zinc-600 sm:hidden">Swipe →</span>
          </div>

          <div className="mt-7 -mx-5 px-5 md:mx-0 md:px-0 flex md:grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 overflow-x-auto md:overflow-visible snap-x snap-mandatory scroll-pl-5 md:scroll-pl-0 no-scrollbar items-start">
            {samples.map((s, i) => (
              <motion.figure
                key={s.src || s.kind}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.65, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`local-visibility-sample-${i}`}
                className="snap-start flex-shrink-0 w-[72%] sm:w-[60%] md:w-auto"
              >
                <div className="aspect-[4/5] rounded-2xl overflow-hidden card-light flex items-center">
                  {s.kind === "gbp" ? <GbpPostCard /> : <img src={s.src} alt={s.alt} loading="lazy" className="w-full h-auto" />}
                </div>
                <figcaption className="mt-3 flex flex-col gap-0.5 text-[12.5px]">
                  <span className="text-zinc-900">{s.client}</span>
                  <span className="text-zinc-600">{s.note}</span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>

        {/* Comparison strip */}
        <div
          data-testid="plan-comparison"
          className="mt-12 sm:mt-20 card-light rounded-2xl divide-y lg:divide-y-0 lg:divide-x divide-zinc-200 grid lg:grid-cols-3"
        >
          <div className="p-5 sm:p-7">
            <div className="text-[11px] tracking-[0.22em] uppercase text-brand-700">Website</div>
            <div className="mt-3 flex items-baseline flex-wrap gap-x-2 text-zinc-900">
              <span className="font-display font-bold text-2xl tracking-tight">$499</span>
              <span className="text-[13px] text-zinc-600">one-time build</span>
              <span className="text-zinc-300 mx-1">+</span>
              <span className="font-display font-bold text-2xl tracking-tight">$79</span>
              <span className="text-[13px] text-zinc-600">/month</span>
            </div>
            <p className="mt-2 text-[13px] text-zinc-600">Build, hosting, maintenance, email lead alerts, client portal.</p>
          </div>
          <div className="p-6 sm:p-7">
            <div className="text-[11px] tracking-[0.22em] uppercase text-brand-700">Google Business Profile setup</div>
            <div className="mt-3 flex items-baseline flex-wrap gap-x-2 text-zinc-900">
              <span className="font-display font-bold text-2xl tracking-tight">${PRICES.gbpSetup}</span>
              <span className="text-[13px] text-zinc-600">one-time · per location</span>
            </div>
            <p className="mt-2 text-[13px] text-zinc-600">
              Setup or cleanup, done once. No subscription.{" "}
              <Link to="/google-business-profile" className="text-brand-700 underline underline-offset-4 hover:text-brand-500">Details</Link>
            </p>
          </div>
          <div className="p-6 sm:p-7">
            <div className="text-[11px] tracking-[0.22em] uppercase text-brand-700">Local Visibility</div>
            <div className="mt-3 flex items-baseline flex-wrap gap-x-2 text-zinc-900">
              <span className="font-display font-bold text-2xl tracking-tight">${PRICES.onboarding}</span>
              <span className="text-[13px] text-zinc-600">onboarding</span>
              <span className="text-zinc-300 mx-1">+</span>
              <span className="font-display font-bold text-2xl tracking-tight">${PRICES.monthly}</span>
              <span className="text-[13px] text-zinc-600">/month</span>
            </div>
            <p className="mt-2 text-[13px] text-zinc-600">Four content pieces a month on Google and one social platform; both platforms ${PRICES.monthly + PRICES.addon}/mo.</p>
          </div>
        </div>
        <p className="mt-4 text-[13px] text-zinc-600">
          Three separate services, billed separately. Take any one on its own or combine them — none of them require a
          WebJesters website. Client portal access is included only with a WebJesters-built website.
        </p>
      </div>
      <VisibilityEnrollModal open={enrollOpen} onClose={() => setEnrollOpen(false)} />
    </section>
  );
}
